/**
 * Vista Mis Réditos — producción del nodo + comisión estimada del promotor +
 * pendiente de cobro. Read-only, preview informativo (NO una liquidación).
 * Consume GET /api/cashier/relatorio?desde=&hasta=.
 */

import { t } from '../languages.js';
import { rangoPorDefecto, montoDisplay, montoHeroHtml } from './_helpers.js';
import { ajustarTextoAlAncho } from '../systems/ajustar-texto-al-ancho.js';
import { contarMonto, celebrarLogro, mejoroRespectoAGuardado } from '../systems/motion.js';
import { renderCardNotificaciones } from './card-notificaciones.js';
import { renderSeccionMisPagos } from './seccion-mis-pagos.js';
import { renderChipsFecha } from './chips-fecha.js';

export function renderReditos({ apiGet }) {
    const el = document.createElement('div');
    const { desde, hasta } = rangoPorDefecto();

    // Card de opt-in de notificaciones (solo si corresponde — ver el componente).
    const cardNotif = renderCardNotificaciones();
    if (cardNotif) el.appendChild(cardNotif);

    const cardReditos = document.createElement('div');
    cardReditos.innerHTML = `
        <div class="card">
            <h2>${t('reditos.titulo')}</h2>
            <div class="date-range">
                <input type="date" id="pm-desde" value="${desde}" aria-label="${t('common.desde')}">
                <input type="date" id="pm-hasta" value="${hasta}" aria-label="${t('common.hasta')}">
            </div>
            <div id="pm-reditos-body">
                <div style="text-align:center; color:var(--text-dim); padding:24px;">${t('common.cargando')}</div>
            </div>
        </div>`;
    el.appendChild(cardReditos);

    // Sección "Mis pagos" (histórico de cobros) — se carga sola.
    el.appendChild(renderSeccionMisPagos({ apiGet }));

    const body = el.querySelector('#pm-reditos-body');
    const desdeEl = el.querySelector('#pm-desde');
    const hastaEl = el.querySelector('#pm-hasta');

    // Chips de atajos de fecha (Hoy/7d/30d) encima de los inputs.
    const chips = renderChipsFecha({ desdeEl, hastaEl, onChange: () => cargar() });
    desdeEl.closest('.date-range').insertAdjacentElement('beforebegin', chips);

    let cargando = false;
    async function cargar() {
        if (cargando) return;
        cargando = true;
        body.innerHTML = `<div style="text-align:center; color:var(--text-dim); padding:24px;">${t('common.cargando')}</div>`;
        const r = await apiGet(`/api/cashier/relatorio?desde=${desdeEl.value}&hasta=${hastaEl.value}`);
        cargando = false;
        if (!r || r.ok === false) {
            body.innerHTML = `<div style="text-align:center; color:var(--danger); padding:24px;">${(r && r.mensaje) || t('common.reintentar')}</div>`;
            return;
        }
        const com = r.comision || {};
        const pendiente = r.pendiente_de_cobro || 0;
        const proximoPago = r.proximo_pago || null;
        const emb = r.embudo_resumen || { captados: 0, registrados: 0, jugando: 0 };
        const tend = r.tendencia_semanal || { actual: 0, previa: 0, direccion: 'neutro' };
        // Tendencia: flecha + color según suba/baje. Más motivador que un lifetime
        // estático — "conversación visual" que incentiva a captar más esta semana.
        const flecha =
            tend.direccion === 'sube' ? '▲' : tend.direccion === 'baja' ? '▼' : '▬';
        const colorTend =
            tend.direccion === 'sube'
                ? 'var(--success)'
                : tend.direccion === 'baja'
                  ? 'var(--danger)'
                  : 'var(--text-dim)';

        // Investigación de dashboards de afiliados (Stake/Revolut/PicPay): el HÉROE
        // es SU plata, no métricas de la casa (Apostado/GGR = ruido que desmotiva).
        // Mostramos: comisión del período (héroe) + A receber / Já recebido + próximo
        // pago + embudo simple (lo único que el promotor controla y puede optimizar).
        body.innerHTML = `
            <div class="card-hero" id="pm-hero-card" style="margin-bottom:10px;">
                <span class="hero-monto" id="pm-hero-redito">${montoHeroHtml(0)}</span>
                <div class="hero-label">${t('reditos.comision')} · ${com.porcentaje || 0}% ${t('reditos.delPeriodo')}</div>
            </div>

            <div class="kpi-grid" style="margin-bottom:10px;">
                <div class="kpi pm-kpi-receber">
                    <div class="label">${t('reditos.aReceber')}</div>
                    <div class="value" style="color:${pendiente > 0 ? 'var(--success)' : 'var(--text)'}">${montoDisplay(pendiente)}</div>
                </div>
                <div class="kpi">
                    <div class="label">${t('reditos.estaSemana')}</div>
                    <div class="value" style="display:flex; align-items:center; gap:6px;">
                        ${montoDisplay(tend.actual)}
                        <span style="color:${colorTend}; font-size:0.7em;">${flecha}</span>
                    </div>
                </div>
            </div>
            <div class="pm-tend-msg" style="color:${colorTend};">${mensajeTendencia(tend, t)}</div>

            ${
                proximoPago
                    ? `<div class="pm-proximo-pago">
                <div class="pm-prox-icon">${iconCalendar()}</div>
                <div style="flex:1; min-width:0;">
                    <div class="pm-prox-label">${t('reditos.proximoPago')}</div>
                    <div class="pm-prox-fecha">${escHtml(proximoPago.fecha_proxima)}</div>
                </div>
            </div>`
                    : ''
            }

            <div class="card" style="margin-bottom:10px;">
                <h2>${t('reditos.tuEmbudo')}</h2>
                <div class="pm-embudo-mini">
                    <div class="pm-emini"><div class="pm-emini-num">${emb.captados}</div><div class="pm-emini-lbl">${t('reditos.captados')}</div></div>
                    <span class="pm-emini-flecha">›</span>
                    <div class="pm-emini"><div class="pm-emini-num">${emb.registrados}</div><div class="pm-emini-lbl">${t('reditos.registrados')}</div></div>
                    <span class="pm-emini-flecha">›</span>
                    <div class="pm-emini"><div class="pm-emini-num" style="color:var(--success);">${emb.jugando}</div><div class="pm-emini-lbl">${t('reditos.jugando')}</div></div>
                </div>
            </div>

            <p style="font-size:14px; color:var(--text-dim); margin:6px 0 0;">${t('reditos.preview')}</p>`;

        // Letra grande + count-up: el rédito cuenta hacia arriba desde 0 y, al
        // llegar, fitty lo escala para LLENAR su ancho (una sola vez, al frame final).
        const heroEl = body.querySelector('#pm-hero-redito');
        const redito = com.redito_estimado || 0;
        if (heroEl) {
            contarMonto(heroEl, redito, {
                onFinal: () => {
                    ajustarTextoAlAncho(heroEl, { minSize: 28, maxSize: 76 });
                    // Micro-celebración SOLO si el rédito mejoró vs. la última vez
                    // (delta positivo) — alineado a la meta real del promotor, no en
                    // cada render (eso mataría el peak-end).
                    if (redito > 0 && mejoroRespectoAGuardado('redito', redito, 'subio')) {
                        const card = body.querySelector('#pm-hero-card');
                        if (card) celebrarLogro(card, 'oro');
                    }
                },
            });
        }
    }

    desdeEl.addEventListener('change', cargar);
    hastaEl.addEventListener('change', cargar);
    cargar();

    return el;
}

function escHtml(s) {
    return String(s == null ? '' : s).replace(
        /[&<>"']/g,
        c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
}
function iconCalendar() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18M8 2v4M16 2v4"/></svg>';
}

// Mensaje que INCENTIVA según la tendencia semanal — la "conversación visual" que
// empuja al promotor a captar más. Sube = celebrar + seguir; baja/neutro = empujar.
function mensajeTendencia(tend, t) {
    if (tend.direccion === 'sube') return t('reditos.tendSube');
    if (tend.direccion === 'baja') return t('reditos.tendBaja');
    return t('reditos.tendNeutro');
}
